import React, { forwardRef, type ReactNode } from 'react';

import classnames from 'classnames';

import { useSize } from '../../features/config';
import { Spinner, type SpinnerProps } from '../Spinner';

import { ButtonBase, type ButtonBaseProps } from './ButtonBase';
import { ButtonContent, type ButtonContentProps } from './ButtonBase/ButtonContent';

import styles from './Button.module.scss';

export type ButtonShape = 'default' | 'square' | 'pill';

type BaseProps = {
    shape?: ButtonShape;
    /**
     * Icon to display at the start of the button.
     */
    icon?: ReactNode;
    /**
     * Icon to display at the end of the button.
     */
    endIcon?: ReactNode;
    /**
     * Content to display at the start of the button.
     */
    start?: ReactNode;
    /**
     * Content to display at the end of the button.
     */
    end?: ReactNode;
    slotProps?: {
        spinner?: Partial<SpinnerProps>;
        content?: Partial<ButtonContentProps>;
    };
};

export type ButtonProps = Omit<ButtonBaseProps, keyof BaseProps> & BaseProps;

export const Button = forwardRef<HTMLElement, ButtonProps>(
    (
        {
            className,
            type,
            size: sizeProp,
            color,
            loading,
            children,
            icon,
            endIcon,
            start,
            end,
            disabled,
            shape = 'default',
            slotProps: { spinner: spinnerProps, content: contentProps } = {},
            ...props
        }: ButtonProps,
        ref
    ) => {
        const size = useSize(sizeProp, 'large', 'Button');
        return (
            <ButtonBase
                ref={ref as React.RefObject<HTMLButtonElement>}
                type={type}
                color={color}
                loading={loading}
                disabled={disabled}
                className={classnames(styles[shape], className)}
                size={size}
                {...(props as ButtonBaseProps)}
            >
                {loading ? (
                    <ButtonContent {...contentProps}>
                        <Spinner size={size === 'large' ? 24 : 16} {...spinnerProps} />
                    </ButtonContent>
                ) : (
                    <>
                        {start && start}
                        <ButtonContent {...contentProps}>
                            {icon && <span>{icon}</span>}
                            {children}
                            {endIcon && <span>{endIcon}</span>}
                        </ButtonContent>
                        {end && end}
                    </>
                )}
            </ButtonBase>
        );
    }
);

Button.displayName = 'Button';
